import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {AccountVerificationService} from '../../account-verification.service';
import {SmsVerificationComponent} from 'src/app/public/sms-verification/sms-verification.component';
import {Channel} from '../../public/signup/signup.component';
import {CheckoutService} from '../checkout.service';
import {ProfileService} from 'src/app/profile.service';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {DynamicClassLoader} from 'src/app/profile/dynamic.class.loader';
import {AddInfoComponent} from 'src/app/profile/add-info/add-info.component';
import {ProfileItemModelService} from 'src/app/profile-item-model.service';
import {DefaultMCConfig} from 'src/app/consts/mc-config.const';
import {MCConfigGroup} from 'src/app/profile/models/mc.group.config.model';
import {AccessToken} from 'src/app/models/accessToken';
import {getUserId} from '../../consts/utilities';
import {HttpStatusCode} from '@angular/common/http';
import {MatBottomSheet, MatBottomSheetRef} from '@angular/material/bottom-sheet';

@Component({
    selector: 'app-verify-checkout',
    templateUrl: './verify-checkout.component.html',
    styleUrls: ['./verify-checkout.component.scss']
})
export class VerifyCheckoutComponent implements OnInit, OnChanges {

    @Output() cartStep = new EventEmitter<number>();
    @Output() checkoutStep = new EventEmitter<number>();

    @Input() expressMode = false;
    @Input() navigationActions = true;

    @Input() emailValidation: boolean;
    @Input() phoneValidation: boolean;

    userPreselectedAccount = false;
    userSelectedAccount = false;

    @ViewChild('codeVerification', {static: true})
    smsVerification: SmsVerificationComponent;

    emails: any[] = [];
    phones: any[] = [];

    // This config is required to setup adding/edition profile forms
    configurations: any = DefaultMCConfig;

    selectedEmail: any = null;
    selectedPhone: any = null;

    customTitle = 'Cargando...';

    @Output() validationResponse = new EventEmitter<any>();

    constructor(
        private checkoutService: CheckoutService,
        private accountVerificationService: AccountVerificationService,
        private profileService: ProfileService,
        private profileModelService: ProfileItemModelService,
        private dialog: MatDialog,
        private bottomSheet: MatBottomSheet,
    ) {
    }

    ngOnInit() {
        this.getConfigurations();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const emailVal = changes.emailValidation.currentValue;
        const phoneVal = changes.phoneValidation.currentValue;

        if (emailVal || phoneVal) {
            this.setupViewBasedOnValidationStatus(emailVal, phoneVal);
        }
    }

    get userId(): string {
        return getUserId();
    }

    private setupViewBasedOnValidationStatus(reqEmail: boolean, reqPhone: boolean): void {
        if (reqEmail) {
            this.customTitle = 'Seleccione correo';
            this.getUserEmails();
        }

        if (reqPhone) {
            this.customTitle = 'Seleccione num. de teléfono';
            this.getUserPhones();
        }
    }

    onCartStep(): void {
        this.cartStep.emit(2);
    }

    onCheckoutStep(): void {
        this.checkoutStep.emit(3);
    }

    private getUserEmails(): void {
        this.profileService.emailAPI('SL', null).subscribe(
            response => this.onGettingEmailsResponse(response),
            error => this.onGettingEmailsError(error)
        );
    }

    private onGettingEmailsResponse(response: any): void {
        this.emails = response.data;
    }

    private onGettingEmailsError(error: Error): void {
        console.error(error);
    }

    private getUserPhones(): void {
        this.profileService.phoneAPI('SL', null).subscribe(
            response => this.onGettingPhonesResponse(response),
            error => this.onGettingPhonesError(error)
        );
    }

    private onGettingPhonesResponse(response: any): void {
        this.phones = response.data;
    }

    private onGettingPhonesError(error: Error): void {
        console.error(error);
    }

    private getConfigurations(): void {
        this.profileModelService.getProfileModel().subscribe(
            response => this.onGettingConfigResponse(response),
            error => this.onGettingConfigError(error)
        );
    }

    private onGettingConfigResponse(response: any): void {
        const configurations = {};

        const {data} = response;

        data.forEach((config: MCConfigGroup) => {
            const type = config.type;
            configurations[type] = config;
        });

        this.configurations = configurations;
    }

    private onGettingConfigError(error: Error): void {
        console.error(error);
    }

    sendValidationCode(): void {
        if (!this.selectedEmail && !this.selectedPhone) {
            // select account alert
            return;
        }

        const channel = (this.phoneValidation)
            ? Channel.SMS
            : Channel.Email;
        const userId = (channel === Channel.SMS)
            ? this.selectedPhone.id_cliente
            : this.selectedEmail.id_cliente;
        const account = (channel === Channel.Email)
            ? this.selectedEmail.email
            : `${this.selectedPhone.pais}${this.selectedPhone.telefono}`;

        this.accountVerificationService.sendLinkCode(userId, account).subscribe(
            response => this.onSendedCodeResponse(response),
            error => this.onSendedCodeError(error)
        );
    }

    private onSendedCodeResponse(response: any): void {
        const { statusCode } = response;

        if (statusCode === HttpStatusCode.Ok) {
            // Account confirmed and code received
            this.userSelectedAccount = true;
            this.openCodeVerification();
        }
    }

    private onSendedCodeError(error: Error): void {
        console.log('Checkout validation failed. More details below.');
        console.error(error);
    }

    setSelectedEmail(email: any): void {
        this.selectedEmail = email;
        this.userPreselectedAccount = true;
    }

    setSelectedPhone(phone: any): void {
        this.selectedPhone = phone;
        this.userPreselectedAccount = true;
    }

    private openCodeVerification(): void {
        const account = (this.selectedEmail !== null)
            ? this.selectedEmail.email
            : `${this.selectedPhone.pais}${this.selectedPhone.telefono}`;

        this.customTitle = `Ingresa el código enviado a ${account}`;

        this.smsVerification.open(
            {
                id: parseInt(this.userId, 10),
                channel: (!this.phoneValidation) ? Channel.Email : Channel.SMS,
                account: account,
                state: 'sendingCode',
                typeOfVerification: 'non-signup',
                isForReset: false
            }
        );
    }

    onUserVerifiedCode(event: any): void {
        // console.log('Checkout validation passed successful.');
        this.validationResponse.emit(event);
        // this.onCheckoutStep();
    }

    openAddingDialog(
        type: string,
        className: string
    ): void {
        // const isMobile = mobile();
        const classInstance = new DynamicClassLoader(className);
        const config = {
            data: {
                type,
                payload: classInstance,
                model: this.configurations[type],
                action: 'IN',
            }
        };

        const dialog = this.dialog.open(AddInfoComponent, config);

        dialog.afterClosed().subscribe(
                response => this.checkAddingResult(response)
        );

        // const dialog: MatDialogRef<AddInfoComponent> | MatBottomSheetRef = isMobile
        //     ? this.bottomSheet.open(AddInfoComponent, config)
        //     : this.dialog.open(AddInfoComponent, config);
        //
        // if (!isMobile && dialog instanceof MatDialogRef) {
        //     dialog.afterClosed().subscribe(
        //         response => this.checkAddingResult(response)
        //     );
        // }
        //
        // if (isMobile && dialog instanceof MatBottomSheetRef) {
        //     dialog.afterDismissed().subscribe(response => this.checkAddingResult(response));
        // }
    }

    private checkAddingResult(response: any): void {
        if (response.canceled) {
            return;
        }

        this.requestAdding(response);
    }

    private addInData(result: any, type: string): void {
        switch (type) {
            case 'email':
                this.emails.push(result.data[0]);
                break;
            case 'telefono':
                this.phones.push(result.data[0]);
                break;
        }
    }

    private updatePayload(model: any, payload: any, data: any): any {
        const template = this.parseJson(model.template);

        template.forEach(item => {
            const newValue = data[item.id];
            payload[item.id] = newValue;
        });

        return JSON.stringify(payload);
    }

    private requestAdding(response: any): void {
        const {type, data, payload, _, model} = response;
        const newPayload = this.updatePayload(model, payload, data);
        const event = model.caller;

        this[event]('IN', newPayload, (result: any) => {
            const {data: cmxData} = <any>result;
            const { codigo } = cmxData[0];

            if (codigo === 0) {
                this.addInData(result, type);
            }

            if (codigo !== 0) {
                // this.onAddedErr(result, type);
            }
        });
    }

    // REQUESTED BY: Addition / Edition Assistant (DON'T REMOVE OR COMMENT THIS CODE)
    private emailRequest(
        paction: string,
        email: any,
        onResponse: (result: any) => void
    ): void {
        this.profileService.emailAPI(paction, email).subscribe(
            (response: any) => onResponse(response),
            error => console.error(error)
        );
    }

    // REQUESTED BY: Addition / Edition Assistant (DON'T REMOVE OR COMMENT THIS CODE)
    private phoneRequest(
        paction: string,
        phone: any,
        onResponse: (result: any) => void
    ): void {
        this.profileService.phoneAPI(paction, phone).subscribe(
            (response: any) => onResponse(response),
            error => console.error(error)
        );
    }

    parseJson(json: string): any {
        return JSON.parse(json);
    }
}
